import type { PaintContext } from "./IRenderer"

const cssToken = (name: string, fallback: string) => {
  if (typeof document === "undefined") return fallback
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim()
  return value || fallback
}

export class Canvas2DPaintContext implements PaintContext {
  private textCache = new Map<string, number>()
  private defaultTextColor = cssToken("--foreground", "#fafafa")
  private defaultLineColor = cssToken("--muted-foreground", "#a1a1a1")

  constructor(private ctx: CanvasRenderingContext2D) {}

  private measureCached(text: string, font: string): number {
    const key = `${font}|${text}`
    let w = this.textCache.get(key)
    if (w === undefined) {
      this.ctx.font = font
      w = this.ctx.measureText(text).width
      this.textCache.set(key, w)
      // Evict old entries to prevent unbounded growth
      if (this.textCache.size > 2000) {
        const iter = this.textCache.keys()
        for (let i = 0; i < 500; i++) iter.next()
        // Just clear — simpler than partial eviction
        this.textCache.clear()
      }
    }
    return w
  }

  rect(
    x: number,
    y: number,
    w: number,
    h: number,
    opts: { fill?: string; stroke?: string; radius?: number; dash?: number[] },
  ) {
    const { ctx } = this
    const r = opts.radius ?? 0

    if (opts.dash) ctx.setLineDash(opts.dash)

    ctx.beginPath()
    if (r > 0) {
      ctx.roundRect(x, y, w, h, r)
    } else {
      ctx.rect(x, y, w, h)
    }

    if (opts.fill) {
      ctx.fillStyle = opts.fill
      ctx.fill()
    }
    if (opts.stroke) {
      ctx.strokeStyle = opts.stroke
      ctx.lineWidth = 1
      ctx.stroke()
    }

    if (opts.dash) ctx.setLineDash([])
  }

  text(
    x: number,
    y: number,
    text: string,
    opts?: {
      font?: string
      color?: string
      maxWidth?: number
      align?: "left" | "center" | "right"
    },
  ): string {
    const { ctx } = this
    const font = opts?.font ?? "13px system-ui, sans-serif"
    const color = opts?.color ?? this.defaultTextColor
    const maxWidth = opts?.maxWidth
    const align = opts?.align ?? "left"

    ctx.font = font
    ctx.fillStyle = color
    ctx.textBaseline = "top"
    ctx.textAlign = align

    let display = text
    if (maxWidth && this.measureCached(text, font) > maxWidth) {
      // Binary search for truncation point instead of character-by-character
      let lo = 0
      let hi = text.length
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1
        if (this.measureCached(`${text.slice(0, mid)}…`, font) <= maxWidth) {
          lo = mid
        } else {
          hi = mid - 1
        }
      }
      display = lo > 0 ? `${text.slice(0, lo)}…` : "…"
    }

    ctx.fillText(display, x, y, maxWidth)
    ctx.textAlign = "left"
    return display
  }

  line(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    opts?: { color?: string; width?: number },
  ) {
    const { ctx } = this
    ctx.strokeStyle = opts?.color ?? this.defaultLineColor
    ctx.lineWidth = opts?.width ?? 1
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
  }

  measureText(text: string, font?: string): number {
    return this.measureCached(text, font ?? "13px system-ui, sans-serif")
  }

  save() {
    this.ctx.save()
  }
  restore() {
    this.ctx.restore()
  }

  clip(x: number, y: number, w: number, h: number) {
    const { ctx } = this
    ctx.beginPath()
    ctx.rect(x, y, w, h)
    ctx.clip()
  }

  alpha(a: number) {
    this.ctx.globalAlpha = a
  }
}
